import React from "react";

interface JsonViewerProps {
  data: any;
  isDark?: boolean;
}

const JsonViewer: React.FC<JsonViewerProps> = ({ data, isDark = false }) => {
  const formatJson = (obj: any, indent: number = 0): React.ReactNode => {
    const spaces = "  ".repeat(indent);

    if (obj === null) {
      return <span className="text-gray-500">null</span>;
    }

    if (typeof obj === "string") {
      return (
        <span className="text-green-600 dark:text-green-400">"{obj}"</span>
      );
    }

    if (typeof obj === "number") {
      return <span className="text-blue-600 dark:text-blue-400">{obj}</span>;
    }

    if (typeof obj === "boolean") {
      return (
        <span className="text-purple-600 dark:text-purple-400">
          {obj.toString()}
        </span>
      );
    }

    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        return <span className="text-gray-500">[]</span>;
      }

      return (
        <div>
          <span className="text-gray-500">[</span>
          <div className="ml-4">
            {obj.map((item, index) => (
              <div key={index}>
                {formatJson(item, indent + 1)}
                {index < obj.length - 1 && (
                  <span className="text-gray-500">,</span>
                )}
              </div>
            ))}
          </div>
          <span className="text-gray-500">{spaces}]</span>
        </div>
      );
    }

    if (typeof obj === "object") {
      const keys = Object.keys(obj);
      if (keys.length === 0) {
        return <span className="text-gray-500">{"{}"}</span>;
      }

      return (
        <div>
          <span className="text-gray-500">{"{"}</span>
          <div className="ml-4">
            {keys.map((key, index) => (
              <div key={key}>
                <span className="text-red-600 dark:text-red-400">"{key}"</span>
                <span className="text-gray-500">: </span>
                {formatJson(obj[key], indent + 1)}
                {index < keys.length - 1 && (
                  <span className="text-gray-500">,</span>
                )}
              </div>
            ))}
          </div>
          <span className="text-gray-500">
            {spaces}
            {"}"}
          </span>
        </div>
      );
    }

    return <span className="text-gray-500">{String(obj)}</span>;
  };

  return (
    <div
      className={`max-h-96 overflow-auto rounded-lg border p-4 font-mono text-sm ${
        isDark ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"
      }`}
    >
      <pre className="whitespace-pre-wrap">{formatJson(data)}</pre>
    </div>
  );
};

export default JsonViewer;
